/* ===================================================================
   useReviews() -- live reviews for the signed-in client.

   Binds to GET /api/v1/reviews (engine/api/v1/app.py) through the typed
   api client (src/lib/api.ts). Brad's Reviews/Dashboard screens render
   mock data today; when they swap to live, they call this hook and map
   Review -> ReviewItem (the UI shape) at the screen boundary.

   AUTH: api.ts attaches the Bearer rr_ API key from localStorage. The
   query is disabled until a token exists, so it never fires a guaranteed
   401 on first paint. (The rr_ key is issued from the dashboard; that
   issuance flow is separate from Supabase login.)
   =================================================================== */
import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { api, type Review, type ReviewsResponse } from '../lib/api';
import { useApiToken } from './useApiToken';
import { useAdminView } from '../admin/adminViewContext';
import { qk } from './queryKeys';

export interface UseReviewsParams {
  since?: string; // YYYY-MM-DD
  platform?: Review['platform'];
  limit?: number;
  /** Force-disable even if a token exists (e.g. screen not ready). */
  enabled?: boolean;
}

export function useReviews(
  params: UseReviewsParams = {},
): UseQueryResult<ReviewsResponse, Error> {
  const { enabled, ...query } = params;
  const hasToken = Boolean(useApiToken());
  const admin = useAdminView();

  // OPTION A: in an operator read-only view, read the admin-scoped reviews for
  // the viewed tenant (Supabase-JWT auth, @require_admin) instead of the
  // signed-in client's own rr_-token reviews. Same ReviewsResponse shape.
  return useQuery({
    queryKey: admin
      ? (['admin', 'tenant', admin.clientId, 'reviews'] as const)
      : qk.reviews(query),
    queryFn: ({ signal }) =>
      admin ? api.getTenantReviews(admin.clientId, signal) : api.listReviews(query, signal),
    enabled: (admin ? true : hasToken) && enabled !== false,
    staleTime: 30_000,
  });
}

/* Convenience selector: just the array (or [] while loading). */
export function useReviewList(params: UseReviewsParams = {}): Review[] {
  return useReviews(params).data?.reviews ?? [];
}
